import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/context/AuthContext';
import { Package, User, LogOut } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AuthModal from '@/components/AuthModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Account = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    if (!user) {
      setShowAuth(true);
    } else {
      fetchOrders();
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'text-green-400';
      case 'shipped': return 'text-blue-400';
      case 'processing': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const getPaymentStatusColor = (status) => {
    return status === 'paid' ? 'text-green-400' : 'text-yellow-400';
  };

  if (!user && !showAuth) {
    return null;
  }

  return (
    <div className="min-h-screen relative">
      <div className="stars"></div>
      <Navbar />

      <div className="relative z-10 pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="glass-card" data-testid="account-sidebar">
                <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-700">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-bold">{user?.name}</div>
                    <div className="text-sm text-gray-400">{user?.email}</div>
                  </div>
                </div>
                <nav className="space-y-2">
                  <button className="w-full text-left px-4 py-3 rounded-lg bg-purple-500/20 border border-purple-500/30 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    My Orders
                  </button>
                  {user?.is_admin && (
                    <button 
                      onClick={() => navigate('/admin')}
                      className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-800 flex items-center gap-2"
                      data-testid="admin-panel-link"
                    >
                      Admin Panel
                    </button>
                  )}
                  <button 
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-800 text-red-400 flex items-center gap-2"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </nav>
              </div>
            </div>

            {/* Orders */}
            <div className="lg:col-span-3">
              <h1 className="text-4xl font-bold mb-8">My Orders</h1>
              
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner"></div>
                </div>
              ) : orders.length === 0 ? (
                <div className="glass-card text-center py-12" data-testid="no-orders">
                  <Package className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                  <h3 className="text-xl font-bold mb-2">No Orders Yet</h3>
                  <p className="text-gray-400 mb-6">Start shopping to see your orders here</p>
                  <button onClick={() => navigate('/shop')} className="btn-primary">
                    Browse Products
                  </button>
                </div>
              ) : (
                <div className="space-y-4" data-testid="orders-list">
                  {orders.map((order) => (
                    <div key={order.id} className="glass-card" data-testid={`order-${order.id}`}>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="text-sm text-gray-400">Order ID</div>
                          <div className="font-mono text-sm">{order.id}</div>
                        </div>
                        <div className="text-right">
                          <div className={`font-semibold capitalize ${getStatusColor(order.status)}`}>
                            {order.status}
                          </div>
                          <div className={`text-sm capitalize ${getPaymentStatusColor(order.payment_status)}`}>
                            {order.payment_status}
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2 mb-4">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span>{item.product_name} - {item.size} - {item.color} x {item.quantity}</span>
                            <span className="font-semibold">
                              {order.currency === 'INR' ? '₹' : '$'}{(item.price * item.quantity).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="border-t border-gray-700 pt-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="text-sm text-gray-400">Total Amount</div>
                            <div className="text-xl font-bold">
                              {order.currency === 'INR' ? '₹' : '$'}{order.total_amount.toFixed(2)}
                            </div>
                          </div>
                          <div className="text-sm text-gray-400">
                            {new Date(order.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} onSuccess={() => setShowAuth(false)} />}
    </div>
  );
};

export default Account;
